/**
 * Scenario Service
 * 시나리오 관련 API 호출을 담당하는 서비스
 */

import {
	buildRelativeApiUrl,
	displayBffApiUrl,
	isStudyResultDevLogEnabled,
	logStudyResultApiTurn,
} from '@/config/studyResultDevConsoleLog';
import { isDevNodeEnv, isScenarioGetInfoDebugEnabled } from '@/config/debugApiLog';
import { normalizeBearerTokenForAuthorization } from '@/utils/bearerTokenNormalize';
import { apiClient, ApiError } from './apiClient';
import { buildStudyResultWebApiContextHeaders } from './platformApiClient';
import { fetchWithSessionRefresh } from './authSession';
import type {
	GetScenarioListByUserIdDto,
	GetScenarioListByUserIdResponseDto,
	CreateScenarioRequestDto,
	CreateScenarioResponseDto,
	GetScenarioAdminListResponseDto,
	GetScenarioListAdminRawResponseDto,
	ScenarioAdminDto,
	GetScenarioListByClassIdResponseDto,
	GetScenarioPlayRecordResponseDto,
	GetScenarioTypeListResponseDto,
	GetScenarioThumbnailImageListByTypeResponseDto,
	GetScenarioListByLicenseResponseDto,
	GetScenarioListByOptionResponseDto,
	ScenarioListByOptionItemDto,
	CreateOldScenarioRequestDto,
	CreateOldScenarioResponseDto,
	GetScenarioBasicInfoModeValueItemDto,
	GetScenarioBasicInfoResponseDto,
	UpdateScenarioBasicInfoRequestDto,
	DeleteScenarioResponseDto,
} from '../types/api/scenario';
import type {
	PatientClothingListResponseDto,
	PoseListResponseDto,
} from '../types/api/patient-clothing-pose';

function isRecord(v: unknown): v is Record<string, unknown> {
	return v != null && typeof v === 'object' && !Array.isArray(v);
}

function extractScenarioListArray(obj: Record<string, unknown> | null | undefined): unknown[] | null {
	if (!obj) return null;
	const keys = ['scenarioList', 'scenarios'] as const;
	for (const k of keys) {
		const v = obj[k];
		if (Array.isArray(v)) return v;
	}
	return null;
}

/**
 * GET /scenario/list 응답이 공통 래퍼(`data`/`result`/`value`)·스네이크 케이스·배열 직반환 등으로 달라져도 파싱
 */
export function normalizeGetScenarioListByUserIdResponse(raw: unknown): GetScenarioListByUserIdResponseDto {
	if (raw == null) {
		return { scenarioList: [], totalCount: 0 };
	}
	if (Array.isArray(raw)) {
		const arr = raw as GetScenarioListByUserIdDto[];
		return { scenarioList: arr, totalCount: arr.length };
	}
	if (!isRecord(raw)) {
		return { scenarioList: [], totalCount: 0 };
	}

	const root = raw;

	if (Array.isArray(root.data)) {
		const arr = root.data as GetScenarioListByUserIdDto[];
		const totalCount = Number(root.totalCount ?? arr.length);
		return {
			scenarioList: arr,
			totalCount: Number.isFinite(totalCount) && totalCount >= 0 ? totalCount : arr.length,
		};
	}

	const nested: Record<string, unknown>[] = [
		root,
		isRecord(root.data) ? (root.data as Record<string, unknown>) : null,
		isRecord(root.result) ? (root.result as Record<string, unknown>) : null,
		isRecord(root.body) ? (root.body as Record<string, unknown>) : null,
		isRecord(root.value) ? (root.value as Record<string, unknown>) : null,
	].filter((o): o is Record<string, unknown> => o != null);

	let list: unknown[] | null = null;
	for (const o of nested) {
		list = extractScenarioListArray(o);
		if (list) break;
	}

	const scenarioList = (list ?? []) as GetScenarioListByUserIdDto[];

	let totalCount = 0;
	for (const o of nested) {
		const tc = o.totalCount;
		if (tc != null && String(tc) !== '') {
			const n = Number(tc);
			if (Number.isFinite(n) && n >= 0) {
				totalCount = n;
				break;
			}
		}
	}
	if (scenarioList.length > 0 && totalCount < scenarioList.length) {
		totalCount = scenarioList.length;
	}

	return { scenarioList, totalCount };
}

/**
 * 클래스별 시나리오 리스트 조회
 * Swagger: GET /scenario/{classId}/class
 */
export const getScenarioListByClassId = async (
	classId: number,
	language?: string
): Promise<GetScenarioListByClassIdResponseDto> => {
	try {
		const response = await apiClient.get<GetScenarioListByClassIdResponseDto>(
			`/scenario/${classId}/class`,
			{ params: language ? { language } : {} }
		);
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '클래스 시나리오 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 본인이 생성한 시나리오 목록 조회
 * Swagger: GET /scenario/list
 * 
 * ⚠️ 주의: Admin 페이지에는 부적합 (본인이 생성한 시나리오만 조회)
 */
export const getScenarioListByUserId = async (params: {
	page: number;
	pageSize: number;
	language?: string;
}): Promise<GetScenarioListByUserIdResponseDto> => {
	try {
		const { language, ...rest } = params;
		const response = await apiClient.get<unknown>(`/scenario/list`, {
			params: { ...rest, ...(language ? { language } : {}) },
		});
		return normalizeGetScenarioListByUserIdResponse(response);
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '시나리오 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * PATCH /info 시 카테고리 ID가 응답에 없을 때, 본인 목록에서 동일 scenarioId 행의 categoryId 사용
 */
export const findScenarioCategoryIdFromMyScenarioList = async (
	scenarioId: number,
	opts?: { maxPages?: number; pageSize?: number }
): Promise<number | undefined> => {
	const pageSize = opts?.pageSize ?? 80;
	const maxPages = opts?.maxPages ?? 40;
	for (let page = 1; page <= maxPages; page++) {
		const res = await getScenarioListByUserId({ page, pageSize });
		const list = res.scenarioList ?? [];
		const hit = list.find((s) => Number(s.scenarioId) === Number(scenarioId));
		if (hit?.scenarioCategoryList?.length) {
			const id = hit.scenarioCategoryList[0]?.scenarioCategoryId;
			if (id != null && Number.isFinite(Number(id)) && Number(id) > 0) {
				return Math.trunc(Number(id));
			}
		}
		const total = Math.max(0, res.totalCount ?? 0);
		if (list.length === 0 || page * pageSize >= total) break;
	}
	return undefined;
};

function categoryTitleFromListOption(raw: unknown): string {
	if (raw == null) return '';
	if (typeof raw === 'string') return raw.trim();
	if (isRecord(raw)) {
		const direct = readLocalizedStringish(raw.title, undefined);
		if (direct) return direct;
		for (const key of ['name', 'label', 'categoryTitle', 'scenarioCategoryTitle', 'scenarioCategoryName'] as const) {
			const value = readLocalizedStringish(raw[key], undefined);
			if (value) return value;
		}
		const localized = readLocalizedStringish(raw, undefined);
		if (localized && localized !== '[object Object]') return localized;
	}
	return String(raw).trim();
}

function deviceNamesFromListOption(raw: unknown): string[] {
	if (!Array.isArray(raw)) return [];
	const out: string[] = [];
	for (const d of raw) {
		if (typeof d === 'string') out.push(d);
		else if (isRecord(d)) {
			for (const key of ['title', 'name', 'label', 'deviceName', 'deviceTypeName'] as const) {
				const value = readLocalizedStringish(d[key], undefined);
				if (value) {
					out.push(value);
					break;
				}
			}
		}
		else if (Array.isArray(d)) {
			for (const x of d) {
				if (typeof x === 'string') out.push(x);
				else if (isRecord(x)) {
					const value = readLocalizedStringish(x.title ?? x.name ?? x.label, undefined);
					if (value) out.push(value);
				}
			}
		}
	}
	return out;
}
/** list/option 항목에서 예상 재생 시간(초) 추출 — API 문서 기준: expectedPlayTimeSeconds만 사용 */
function expectedPlaySecondsFromListOptionItem(item: ScenarioListByOptionItemDto): number {
	const n = Number(item.expectedPlayTimeSeconds);
	if (Number.isFinite(n) && n > 0) return Math.trunc(n);
	return 0;
}

/**
 * GET /scenario/list/option → 관리·프로덕트 모달 등에서 쓰는 Admin 목록 형태로 정규화
 */
export function normalizeScenarioListByOptionToAdmin(
	raw: GetScenarioListByOptionResponseDto | unknown
): GetScenarioAdminListResponseDto {
	const nested: Record<string, unknown>[] = isRecord(raw)
		? [
			raw,
			isRecord(raw.data) ? raw.data : null,
			isRecord(raw.result) ? raw.result : null,
			isRecord(raw.body) ? raw.body : null,
			isRecord(raw.value) ? raw.value : null,
		].filter((o): o is Record<string, unknown> => o != null)
		: [];
	const list =
		(Array.isArray(raw) ? raw : null) ??
		nested.map(extractScenarioListArray).find((arr): arr is unknown[] => Array.isArray(arr)) ??
		[];
	const scenarioList: ScenarioAdminDto[] = list.map((unknownItem) => {
		const item = (isRecord(unknownItem) ? unknownItem : {}) as ScenarioListByOptionItemDto & Record<string, unknown>;
		// API 문서 기준: scenarioId만 사용
		const scenarioId = Number.isFinite(Number(item.scenarioId)) ? Math.trunc(Number(item.scenarioId)) : 0;
		// API 문서 기준: title만 사용
		const title =
			(typeof item.title === 'string' ? item.title : '') ||
			(scenarioId > 0 ? `시나리오 #${scenarioId}` : '(제목 없음)');
		// API 문서 기준: categoryTitle만 사용
		const categoryRaw = item.categoryTitle;
		const catTitle = categoryTitleFromListOption(categoryRaw);
		// API 문서 기준: scenarioCategoryId만 사용
		const catId = Number.isFinite(Number(item.scenarioCategoryId)) ? Math.trunc(Number(item.scenarioCategoryId)) : undefined;
		// API 문서 기준: deviceNames (fallback: deviceTypeList)
		const deviceNames = deviceNamesFromListOption(
			item.deviceNames ?? item.deviceTypeList
		);
		const playSec = expectedPlaySecondsFromListOptionItem(item);
		// API 문서 기준: createdAt만 사용
		const createdAt = typeof item.createdAt === 'string' ? item.createdAt : '';
		// API 문서 기준: totalPlayCount만 사용
		const totalPlayCount = Number.isFinite(Number(item.totalPlayCount)) ? Math.trunc(Number(item.totalPlayCount)) : undefined;
		// API 문서 기준: modeValueTitle만 사용
		const modeValueTitle = typeof item.modeValueTitle === 'string' ? item.modeValueTitle : undefined;
		return {
			scenarioId,
			title,
			scenarioCategoryList: catTitle
				? [{ scenarioCategoryId: Number.isFinite(catId) && catId != null ? Math.trunc(catId) : 0, type: '', title: catTitle }]
				: [],
			version: '',
			difficulty: 0,
			createdAt,
			thumbnailImageUrl: '',
			expectedPlayTimeSeconds: playSec,
			status: 'active' as const,
			simulationCount: totalPlayCount != null && Number.isFinite(totalPlayCount) ? Math.trunc(totalPlayCount) : undefined,
			deviceNames,
			...(modeValueTitle ? { modeValueTitle } : {}),
		};
	}).filter((s) => s.scenarioId > 0);
	const totalFromResponse = nested
		.map((o) => Number(o.totalCount))
		.find((n) => Number.isFinite(n) && n >= 0);
	const total = totalFromResponse ?? scenarioList.length;
	return {
		scenarioList,
		totalCount: total,
		stats: { total, active: total, inactive: 0 },
	};
}

function mapUnknownRowToScenarioAdminDto(row: unknown): ScenarioAdminDto | null {
	if (!isRecord(row)) return null;
	const scenarioId = Number(row.scenarioId);
	if (!Number.isFinite(scenarioId) || scenarioId <= 0) return null;
	const title =
		typeof row.title === 'string' ? row.title : String(row.title ?? '').trim() || '(제목 없음)';

	let scenarioCategoryList: ScenarioAdminDto['scenarioCategoryList'] = [];
	if (Array.isArray(row.scenarioCategoryList)) {
		scenarioCategoryList = row.scenarioCategoryList
			.map((c): ScenarioAdminDto['scenarioCategoryList'][0] | null => {
				if (!isRecord(c)) return null;
				const id = Number(c.scenarioCategoryId);
				return {
					scenarioCategoryId: Number.isFinite(id) ? Math.trunc(id) : 0,
					type: typeof c.type === 'string' ? c.type : '',
					title: typeof c.title === 'string' ? c.title : '',
				};
			})
			.filter((x): x is ScenarioAdminDto['scenarioCategoryList'][0] => x != null);
	} else if (row.categoryTitle != null) {
		const ct = categoryTitleFromListOption(row.categoryTitle);
		if (ct) scenarioCategoryList = [{ scenarioCategoryId: 0, type: '', title: ct }];
	}

	let deviceNames = deviceNamesFromListOption(row.deviceNames);
	if (deviceNames.length === 0 && Array.isArray(row.deviceTypeList)) {
		for (const d of row.deviceTypeList) {
			if (isRecord(d) && typeof d.title === 'string' && d.title.trim()) deviceNames.push(d.title.trim());
		}
	}

	// API 문서 기준: createdAt만 사용
	const createdAt =
		typeof row.createdAt === 'string'
			? row.createdAt
			: '';

	return {
		scenarioId: Math.trunc(scenarioId),
		title,
		scenarioCategoryList,
		version: typeof row.version === 'string' ? row.version : '',
		difficulty: Number.isFinite(Number(row.difficulty)) ? Math.trunc(Number(row.difficulty)) : 0,
		createdAt,
		// API 문서 기준: thumbnailImageUrl만 사용
		thumbnailImageUrl:
			typeof row.thumbnailImageUrl === 'string'
				? row.thumbnailImageUrl
				: '',
		// API 문서 기준: expectedPlayTimeSeconds만 사용
		expectedPlayTimeSeconds: (() => {
			const n = Number(row.expectedPlayTimeSeconds);
			return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
		})(),
		status: row.status === 'inactive' ? 'inactive' : 'active',
		// API 문서 기준: totalPlayCount만 사용
		simulationCount:
			Number.isFinite(Number(row.totalPlayCount))
				? Math.trunc(Number(row.totalPlayCount))
				: undefined,
		deviceNames: deviceNames.length > 0 ? deviceNames : undefined,
		// API 문서 기준: modeValueTitle만 사용
		modeValueTitle: typeof row.modeValueTitle === 'string' ? row.modeValueTitle : undefined,
	};
}

/**
 * GET /scenario/list/admin (및 공통 래퍼) 응답 → 프로덕트 커리큘럼 선택 등 Admin 목록 DTO
 */
export function normalizeGetScenarioAdminListResponse(raw: unknown): GetScenarioAdminListResponseDto {
	const emptyStats = { total: 0, active: 0, inactive: 0 };
	if (raw == null) {
		return { scenarioList: [], totalCount: 0, stats: emptyStats };
	}

	if (Array.isArray(raw)) {
		const scenarioList = raw
			.map(mapUnknownRowToScenarioAdminDto)
			.filter((x): x is ScenarioAdminDto => x != null);
		const totalCount = scenarioList.length;
		return {
			scenarioList,
			totalCount,
			stats: { total: totalCount, active: totalCount, inactive: 0 },
		};
	}

	if (!isRecord(raw)) {
		return { scenarioList: [], totalCount: 0, stats: emptyStats };
	}

	const sl = raw.scenarioList;
	if (Array.isArray(sl) && sl.length > 0) {
		const first = sl[0];
		if (
			isRecord(first) &&
			('totalPlayCount' in first || 'no' in first) &&
			!Array.isArray(first.scenarioCategoryList)
		) {
			return normalizeScenarioListByOptionToAdmin(raw as unknown as GetScenarioListByOptionResponseDto);
		}
	}

	const nested: Record<string, unknown>[] = [
		raw,
		isRecord(raw.data) ? (raw.data as Record<string, unknown>) : null,
		isRecord(raw.result) ? (raw.result as Record<string, unknown>) : null,
		isRecord(raw.body) ? (raw.body as Record<string, unknown>) : null,
		isRecord(raw.value) ? (raw.value as Record<string, unknown>) : null,
	].filter((o): o is Record<string, unknown> => o != null);

	let list: unknown[] | null = null;
	for (const o of nested) {
		list = extractScenarioListArray(o);
		if (list) break;
	}

	let totalCount = 0;
	for (const o of nested) {
		const tc = o.totalCount;
		if (tc != null && String(tc) !== '') {
			const n = Number(tc);
			if (Number.isFinite(n) && n >= 0) {
				totalCount = n;
				break;
			}
		}
	}

	const scenarioList = (list ?? [])
		.map(mapUnknownRowToScenarioAdminDto)
		.filter((x): x is ScenarioAdminDto => x != null);

	if (scenarioList.length > 0 && totalCount < scenarioList.length) {
		totalCount = scenarioList.length;
	}

	const active = scenarioList.filter((s) => s.status !== 'inactive').length;
	const inactive = scenarioList.length - active;
	const t = totalCount || scenarioList.length;
	return {
		scenarioList,
		totalCount: t,
		stats: { total: t, active: active || t, inactive },
	};
}

export type GetScenarioListAdminParams = {
	page: number;
	pageSize: number;
	status?: string;
	category?: string;
	platform?: string;
	search?: string;
	startDate?: string;
	endDate?: string;
	language?: string;
};

/**
 * 어드민 전체 시나리오 목록 — Swagger: GET /scenario/list/admin
 * (프로덕트 커리큘럼 추가 모달 등, /scenario/list/option 과 달리 등록된 전체 시나리오 조회)
 */
export const getScenarioListAdmin = async (
	params: GetScenarioListAdminParams
): Promise<GetScenarioListAdminRawResponseDto> => {
	const apiParams: Record<string, string | number> = {
		page: params.page,
		pageSize: params.pageSize,
	};
	if (params.search?.trim()) apiParams.searchText = params.search.trim();
	if (params.startDate) apiParams.startDate = params.startDate;
	if (params.endDate) apiParams.endDate = params.endDate;
	if (params.category != null && params.category !== '' && /^\d+$/.test(String(params.category))) {
		apiParams.scenarioCategoryId = Number(params.category);
	}
	if (params.platform != null && params.platform !== '' && /^\d+$/.test(String(params.platform))) {
		apiParams.deviceTypeId = Number(params.platform);
	}
	if (params.language) apiParams.language = params.language;
	return await apiClient.get<GetScenarioListAdminRawResponseDto>('/scenario/list/admin', {
		params: apiParams,
	});
};

/**
 * Admin·프로덕트용 시나리오 목록
 * Swagger: **GET /scenario/list/option** (존재하지 않는 `/scenario/admin/list` 대체)
 */
export const getScenarioAdminList = async (params: {
	page: number;
	pageSize: number;
	status?: string;
	category?: string;
	platform?: string;
	search?: string;
	startDate?: string;
	endDate?: string;
	language?: string;
}): Promise<GetScenarioAdminListResponseDto> => {
	const apiParams: Record<string, string | number> = {
		page: params.page,
		pageSize: params.pageSize,
	};
	if (params.search?.trim()) apiParams.searchText = params.search.trim();
	if (params.startDate) apiParams.startDate = params.startDate;
	if (params.endDate) apiParams.endDate = params.endDate;
	if (params.category != null && params.category !== '' && /^\d+$/.test(String(params.category))) {
		apiParams.scenarioCategoryId = Number(params.category);
	}
	if (params.platform != null && params.platform !== '' && /^\d+$/.test(String(params.platform))) {
		apiParams.deviceTypeId = Number(params.platform);
	}
	if (params.language) apiParams.language = params.language;
	try {
		const response = await apiClient.get<GetScenarioListByOptionResponseDto>(
			`/scenario/list/option`,
			{
				params: apiParams,
			}
		);
		return normalizeScenarioListByOptionToAdmin(response);
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '시나리오 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 옵션별 시나리오 목록 (원본 DTO)
 * Swagger: GET /scenario/list/option
 */
export const getScenarioListByOption = async (params: {
	page: number;
	pageSize: number;
	scenarioCategoryId?: number;
	deviceTypeId?: number;
	searchText?: string;
	startDate?: string;
	endDate?: string;
	language?: string;
}): Promise<GetScenarioListByOptionResponseDto> => {
	try {
		const { language, ...rest } = params;
		return await apiClient.get<GetScenarioListByOptionResponseDto>(
			`/scenario/list/option`,
			{
				params: { ...rest, ...(language ? { language } : {}) },
			}
		);
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '시나리오 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * BFF가 mode_value / mode_value_list (스네이크)로 줄 때 frond는 camelCase만 읽는 경우가 있어 통일.
 */
function readModeDisplayTitleish(raw: unknown): string | null {
	if (raw == null) return null;
	if (typeof raw === 'string') {
		const t = raw.trim();
		return t || null;
	}
	if (isRecord(raw) && 'value' in raw) {
		const v = (raw as Record<string, unknown>).value;
		if (typeof v === 'string' && v.trim()) return v.trim();
	}
	const s = String(raw).trim();
	return s || null;
}

function normalizeBasicInfoModeValueRow(
	row: unknown
): GetScenarioBasicInfoModeValueItemDto | null {
	if (!isRecord(row)) return null;
	// API 문서 기준: modeValue만 사용
	const modeValue = row.modeValue;
	if (modeValue == null) return null;
	// API 문서 기준: modeValueTitle 우선, fallback으로 name, label, title
	const titleish =
		row.modeValueTitle ??
		row.name ??
		row.label ??
		row.title;
	return {
		modeValue: String(modeValue).trim() || String(modeValue),
		modeValueTitle: readModeDisplayTitleish(titleish),
	};
}

function applyModeFieldsToBasicInfo(
	body: Record<string, unknown>
): Record<string, unknown> {
	// API 문서 기준: modeValueList만 사용
	const listRaw = body.modeValueList;
	const normalized: GetScenarioBasicInfoModeValueItemDto[] = [];
	if (Array.isArray(listRaw)) {
		for (const row of listRaw) {
			const m = normalizeBasicInfoModeValueRow(row);
			if (m) normalized.push(m);
		}
	}
	// API 문서 기준: modeValue, modeValueTitle만 사용
	const topValue = body.modeValue;
	const topTitle = body.modeValueTitle;
	const out: Record<string, unknown> = { ...body };
	if (normalized.length > 0) {
		for (const row of normalized) {
			if (row.modeValue && !String(row.modeValueTitle ?? '').trim()) {
				row.modeValueTitle = row.modeValue;
			}
		}
		out.modeValueList = normalized;
	} else {
		/* 리스트는 없는데 루트에만 스네이크/구필드가 있는 경우 */
		if (topValue != null && String(topValue).trim()) {
			const m: GetScenarioBasicInfoModeValueItemDto = { modeValue: String(topValue).trim() };
			if (topTitle != null) {
				m.modeValueTitle =
					typeof topTitle === 'string' ? topTitle.trim() : String((topTitle as { value?: unknown })?.value ?? topTitle);
			}
			if (!String(m.modeValueTitle ?? '').trim() && m.modeValue) {
				m.modeValueTitle = m.modeValue;
			}
			out.modeValueList = [m];
		}
	}
	/* @deprecated 루트 — camel + 스네이크 둘 다 쓰는 쪽이 있으면 루트에도 맞춤 */
	if (out.modeValue == null && topValue != null) {
		out.modeValue = String(topValue);
	}
	if (out.modeValueTitle == null && topTitle != null) {
		out.modeValueTitle = topTitle;
	}
	if (!String((out as { modeValueTitle?: unknown }).modeValueTitle ?? '').trim() && (out as { modeValue?: unknown }).modeValue != null) {
		(out as { modeValueTitle: string }).modeValueTitle = String((out as { modeValue: unknown }).modeValue);
	}
	return out;
}

/** BFF/백엔드가 string·{ value }·i18n 객체·스네이크 키로 title 등을 줄 때 1문자열로 */
function readLocalizedStringish(v: unknown, language?: string): string {
	if (v == null) return '';
	if (typeof v === 'string') {
		return v.trim();
	}
	if (isRecord(v) && !Array.isArray(v)) {
		if (typeof v.value === 'string' && v.value.trim()) {
			return v.value.trim();
		}
		const lang = language && typeof (v as Record<string, unknown>)[language] === 'string' ? (v as Record<string, unknown>)[language] : null;
		if (typeof lang === 'string' && lang.trim()) return lang.trim();
		for (const k of ['ko', 'en', 'en-US', 'en_US', 'ja', 'mn']) {
			const x = (v as Record<string, unknown>)[k];
			if (typeof x === 'string' && x.trim()) return x.trim();
		}
		for (const x of Object.values(v)) {
			if (typeof x === 'string' && x.trim()) return x.trim();
		}
	}
	const s = String(v ?? '');
	return s === 'undefined' || s === 'null' ? '' : s.trim();
}

function looksLikeScenarioBasicInfoRecord(c: Record<string, unknown>): boolean {
	// API 문서 기준: camelCase만 사용
	return (
		'key' in c ||
		'title' in c ||
		'scenarioId' in c ||
		'contentCode' in c ||
		'modeValueList' in c ||
		'learningObjectives' in c
	);
}

/**
 * key·title·카테고리·학습목록 등 BFF/스네이크 케이스와 별칭을 DTO 쪽 키로 맞춤
 */
function applyCommonBasicInfoFields(
	body: Record<string, unknown>,
	language?: string
): Record<string, unknown> {
	const out: Record<string, unknown> = { ...body };
	// API 문서 기준: title만 사용
	const titleFrom = readLocalizedStringish(out.title, language);
	if (titleFrom) {
		out.title = titleFrom;
	}
	// API 문서 기준: key 또는 contentCode만 사용
	const keyFrom =
		(out.key as string) ||
		((out as { contentCode?: unknown }).contentCode as string);
	if (keyFrom) {
		out.key = String(keyFrom);
	}
	// API 문서 기준: scenarioCategoryId만 사용
	const cat = (out as { scenarioCategoryId?: number }).scenarioCategoryId;
	if (cat != null && Number.isFinite(Number(cat))) {
		(out as { scenarioCategoryId: number }).scenarioCategoryId = Number(cat);
	}
	// API 문서 기준: learningObjectives만 사용
	const obLo = (out as { learningObjectives?: unknown }).learningObjectives;
	if (Array.isArray(obLo)) {
		const los = obLo
			.map((row) => {
				if (!isRecord(row)) {
					if (typeof row === 'string') return { title: row };
					return { title: String(row) };
				}
				const t = row.title;
				if (t == null) return null;
				return { title: readLocalizedStringish(t, language) || String(t) };
			})
			.filter(
				(x): x is { title: string } =>
					x != null && typeof (x as { title?: string }).title === 'string' && (x as { title: string }).title.length > 0
			);
		(out as { learningObjectives: { title: string }[] }).learningObjectives = los;
	}
	// API 문서 기준: handoverNote, deviceTypeIdList는 camelCase만 사용
	return out;
}

/**
 * GET /scenario/{scenarioId}/info 응답을 GetScenarioBasicInfoResponseDto로 정규화.
 * 백엔드가 { data: {...} } 또는 { result: {...} } 공통 래퍼로 감싸서 줄 때도 처리.
 */
function normalizeScenarioBasicInfo(
	raw: unknown,
	language?: string
): GetScenarioBasicInfoResponseDto {
	if (!isRecord(raw)) return raw as unknown as GetScenarioBasicInfoResponseDto;
	let inner: Record<string, unknown> | null = null;
	for (const wrapKey of ['data', 'result', 'body', 'value'] as const) {
		const c = raw[wrapKey];
		if (isRecord(c) && looksLikeScenarioBasicInfoRecord(c)) {
			inner = c;
			break;
		}
	}
	if (!inner) {
		inner = raw;
	}
	if (!isRecord(inner)) {
		return raw as unknown as GetScenarioBasicInfoResponseDto;
	}
	const merged = applyModeFieldsToBasicInfo(applyCommonBasicInfoFields(inner, language)) as unknown as GetScenarioBasicInfoResponseDto;
	return merged;
}

/** 시나리오 기본 정보 조회 — Swagger: GET /scenario/{scenarioId}/info */
export const getScenarioBasicInfo = async (
	scenarioId: number,
	language?: string,
	/** 학습 결과 페이지 등: [API_연동_가이드] medicrew web + `x-class-id` (BFF) */
	studyContextClassId?: string
): Promise<GetScenarioBasicInfoResponseDto> => {
	const path = buildRelativeApiUrl(`/scenario/${scenarioId}/info`, { language: language || undefined });
	const displayUrl = displayBffApiUrl(path);
	const ctx = buildStudyResultWebApiContextHeaders(studyContextClassId);
	if (isStudyResultDevLogEnabled()) {
		const h: Record<string, string> = { ...ctx };
		const token = apiClient.getAccessToken();
		if (token) h['Authorization'] = `Bearer ${normalizeBearerTokenForAuthorization(token)}`;
		logStudyResultApiTurn('req', 'BFF GET /scenario/{id}/info', {
			method: 'GET',
			url: displayUrl,
			...(language ? { params: { language } } : {}),
			headers: h,
		});
	}
	try {
		const raw = await apiClient.get<unknown>(`/scenario/${scenarioId}/info`, {
			params: language ? { language } : {},
			...(Object.keys(ctx).length > 0 ? { headers: ctx } : {}),
		});
		const normalized = normalizeScenarioBasicInfo(raw, language);
		if (isStudyResultDevLogEnabled()) {
			logStudyResultApiTurn('res', 'BFF GET /scenario/{id}/info', {
				url: displayUrl,
				status: 200,
				body: normalized,
			});
		}
		return normalized;
	} catch (error) {
		if (isStudyResultDevLogEnabled()) {
			if (error instanceof ApiError) {
				logStudyResultApiTurn('err', 'BFF GET /scenario/{id}/info', {
					url: displayUrl,
					status: error.status,
					body: error.data,
					err: error.message,
				});
			} else {
				logStudyResultApiTurn('err', 'BFF GET /scenario/{id}/info', { url: displayUrl, err: error });
			}
		}
		if (error instanceof ApiError) {
			throw new Error(error.message || '시나리오 기본 정보를 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

function formatApiValidationMessage(data: unknown): string | undefined {
	if (data == null || typeof data !== 'object') return undefined;
	const m = (data as { message?: unknown }).message;
	if (typeof m === 'string' && m.trim()) return m.trim();
	if (Array.isArray(m) && m.length > 0) {
		const lines = m.filter((x): x is string => typeof x === 'string');
		if (lines.length) return lines.join('\n');
	}
	return undefined;
}

/** 시나리오 기본 정보 수정 — Swagger: PATCH /scenario/{scenarioId}/info */
export const updateScenarioBasicInfo = async (
	scenarioId: number,
	data: UpdateScenarioBasicInfoRequestDto
): Promise<{ message?: string }> => {
	try {
		const res = await apiClient.patch<{ message?: string }>(
			`/scenario/${scenarioId}/info`,
			data
		);
		return res;
	} catch (error) {
		if (error instanceof ApiError) {
			const bodyMsg = formatApiValidationMessage(error.data);
			throw new Error(
				bodyMsg || error.message || '시나리오 기본 정보 수정에 실패했습니다.'
			);
		}
		throw error;
	}
};

/** GET /scenario/{scenarioId}/info 기반 조회 결과 */
export type ScenarioDetailOrBasicResult = {
	source: 'basic';
	data: GetScenarioBasicInfoResponseDto;
};

/**
 * 시나리오 기본정보(/info) 조회.
 * /detail API는 플로우 미구성 시나리오에서 오류가 발생하므로 사용하지 않음.
 */
export const getScenarioDetailWithBasicFallback = async (
	scenarioId: number,
	language?: string,
	studyContextClassId?: string
): Promise<ScenarioDetailOrBasicResult> => {
	const data = await getScenarioBasicInfo(scenarioId, language, studyContextClassId);
	return { source: 'basic', data };
};

/**
 * 시나리오 카테고리 목록 조회
 * Swagger: GET /scenario-category/list
 */
export const getScenarioCategoryList = async (type?: string, language?: string) => {
	try {
		const response = await apiClient.get<any>(`/scenario-category/list`, {
			params: { ...(type ? { type } : {}), ...(language ? { language } : {}) },
		});
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '시나리오 카테고리 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 캐릭터 목록 조회
 * Swagger: GET /character/list
 */
export const getCharacterList = async (type?: string) => {
	try {
		const response = await apiClient.get<any>('/character/list', {
			params: type ? { type } : {},
		});
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '캐릭터 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 시나리오 타입 목록 조회
 * Swagger: GET /scenario-type/list
 */
export const getScenarioTypeList = async (): Promise<GetScenarioTypeListResponseDto> => {
	try {
		const response = await apiClient.get<GetScenarioTypeListResponseDto>('/scenario-type/list');
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '시나리오 타입 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 시나리오 타입별 썸네일 이미지 목록 조회
 * Swagger: GET /scenario-thumbnail-image/list?scenarioTypeId=
 */
export const getScenarioThumbnailImageList = async (
	scenarioTypeId: number
): Promise<GetScenarioThumbnailImageListByTypeResponseDto> => {
	try {
		const response = await apiClient.get<GetScenarioThumbnailImageListByTypeResponseDto>(
			'/scenario-thumbnail-image/list',
			{ params: { scenarioTypeId } }
		);
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '썸네일 이미지 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 라이센스별 시나리오 리스트 조회
 * Swagger: GET /scenario/license
 */
export interface GetScenarioListByLicenseParams {
	organizationLicenseId: number;
	scenarioCategoryId?: number;
	searchKeyword?: string;
	page: number;
	pageSize: number;
}

export const getScenarioListByLicense = async (
	params: GetScenarioListByLicenseParams
): Promise<GetScenarioListByLicenseResponseDto> => {
	try {
		const response = await apiClient.get<GetScenarioListByLicenseResponseDto>('/scenario/license', {
			params,
		});
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '라이센스별 시나리오 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 장치 타입 목록 조회
 * Swagger: GET /device/type/list
 */
export const getDeviceTypeList = async () => {
	try {
		const response = await apiClient.get<any>('/device/type/list');
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '장치 타입 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 맵 아이템 목록 조회
 * Swagger: GET /item/list/option?type=MAP
 */
export const getMapItemList = async () => {
	try {
		const response = await apiClient.get<any>('/item/list/option', {
			params: { type: 'MAP' },
		});
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '맵 아이템 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 스킬·타입별 아이템 리스트 조회
 * Swagger: GET /item/list/option — type 필수 (MAP|CART|EQUIPMENT|CONSUMABLE|MEDICATION|EXPRESSION), skillId 선택
 */
export const getItemListByOption = async (params: {
	type: 'MAP' | 'CART' | 'EQUIPMENT' | 'CONSUMABLE' | 'MEDICATION' | 'EXPRESSION';
	skillId?: number;
}) => {
	try {
		const response = await apiClient.get<any>('/item/list/option', { params });
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '아이템 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 시나리오 플레이 기록 조회
 * Phase 11: GET /scenario-play-record — userId, classId, classUserId, scenarioId, page(기본 1), pageSize(기본 20)
 */
export interface GetScenarioPlayRecordParams {
	/** 숫형 DB id 또는 MongoDB ObjectId 문자열 등 백엔드가 허용하는 형식 */
	userId?: number | string;
	classId?: number;
	classUserId?: number;
	scenarioId?: number;
	page?: number;
	pageSize?: number;
}

export const getScenarioPlayRecords = async (
	params?: GetScenarioPlayRecordParams,
	/** 학습 결과 페이지: `x-class-id` + medicrew web (쿼리 `classId`와 동일·보조) */
	context?: { studyContextClassId?: string }
): Promise<GetScenarioPlayRecordResponseDto> => {
	const query = params
		? {
				...(params.userId != null && { userId: params.userId }),
				...(params.classId != null && { classId: params.classId }),
				...(params.classUserId != null && { classUserId: params.classUserId }),
				...(params.scenarioId != null && { scenarioId: params.scenarioId }),
				page: params.page ?? 1,
				pageSize: params.pageSize ?? 20,
			}
		: { page: 1, pageSize: 20 };
	const pathQ = buildRelativeApiUrl('/scenario-play-record', query);
	const displayUrl = displayBffApiUrl(pathQ);
	const ctx = buildStudyResultWebApiContextHeaders(context?.studyContextClassId);
	try {
		if (isStudyResultDevLogEnabled()) {
			const h: Record<string, string> = { ...ctx };
			const token = apiClient.getAccessToken();
			if (token) h['Authorization'] = `Bearer ${normalizeBearerTokenForAuthorization(token)}`;
			logStudyResultApiTurn('req', 'BFF GET /scenario-play-record', {
				method: 'GET',
				url: displayUrl,
				params: query,
				headers: h,
			});
		}
		const response = await apiClient.get<GetScenarioPlayRecordResponseDto | Record<string, unknown>>(
			'/scenario-play-record',
			{ params: query, ...(Object.keys(ctx).length > 0 ? { headers: ctx } : {}) }
		);
		// 백엔드 응답 키가 다를 수 있음: playRecordList | scenarioPlayRecordList | list
		const raw = response as Record<string, unknown>;
		const list = Array.isArray(raw.playRecordList)
			? raw.playRecordList
			: Array.isArray(raw.scenarioPlayRecordList)
				? raw.scenarioPlayRecordList
				: Array.isArray(raw.list)
					? raw.list
					: [];
		const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : list.length;
		const dto = { playRecordList: list as GetScenarioPlayRecordResponseDto['playRecordList'], totalCount };
		if (isStudyResultDevLogEnabled()) {
			logStudyResultApiTurn('res', 'BFF GET /scenario-play-record', { url: displayUrl, status: 200, body: dto });
		}
		return dto;
	} catch (error) {
		if (isStudyResultDevLogEnabled()) {
			if (error instanceof ApiError) {
				logStudyResultApiTurn('err', 'BFF GET /scenario-play-record', {
					url: displayUrl,
					status: error.status,
					body: error.data,
					err: error.message,
				});
			} else {
				logStudyResultApiTurn('err', 'BFF GET /scenario-play-record', { url: displayUrl, err: error });
			}
		}
		if (error instanceof ApiError) {
			throw new Error(error.message || '플레이 기록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 환자 의상 리스트 조회 (Phase 3)
 * Swagger: GET /patient-clothing/list?type= (TOP | BOTTOM)
 */
export const getPatientClothingList = async (
	type?: 'TOP' | 'BOTTOM'
): Promise<PatientClothingListResponseDto> => {
	try {
		const response = await apiClient.get<PatientClothingListResponseDto>(
			'/patient-clothing/list',
			{ params: type ? { type } : {} }
		);
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '환자 의상 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 환자 포즈 리스트 조회 (Phase 3)
 * Swagger: GET /pose/list?page=&pageSize=
 */
export const getPoseList = async (params: {
	page: number;
	pageSize: number;
}): Promise<PoseListResponseDto> => {
	try {
		const response = await apiClient.get<PoseListResponseDto>('/pose/list', {
			params,
		});
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '환자 포즈 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 시나리오 삭제 — DELETE /scenario/{scenarioId}
 * 403: 본인 시나리오만 삭제, 404: 없음, 409: 프로덕트 사용 중
 */
export const deleteScenario = async (scenarioId: number): Promise<DeleteScenarioResponseDto> => {
	return apiClient.delete<DeleteScenarioResponseDto>(`/scenario/${scenarioId}`);
};

/**
 * 시나리오 생성
 * Swagger: POST /scenario
 * - 백엔드 기대: camelCase (snake_case 시 "property should not exist" 400)
 * - camelCase 전송 시 400이면 원인은 백엔드 검증/스펙(검증 상세 미반환). 프론트 요청 형식은 Swagger 기준 준수.
 */
export const createScenario = async (data: CreateScenarioRequestDto): Promise<CreateScenarioResponseDto> => {
	try {
		const response = await apiClient.post<CreateScenarioResponseDto>('/scenario', data);
		return response;
	} catch (error) {
		if (error instanceof ApiError) {
			if (error.status === 400) {
				throw new Error(error.message || '시나리오 생성 요청이 올바르지 않습니다.');
			}
			if (error.status === 404) {
				throw new Error('아이템, 캐릭터 또는 카테고리를 찾을 수 없습니다.');
			}
			throw new Error(error.message || '시나리오 생성에 실패했습니다.');
		}
		throw error;
	}
};

/**
 * 기존 시트형 시나리오 등록
 * Swagger: POST /scenario/old
 */
export const createOldScenario = async (
	data: CreateOldScenarioRequestDto
): Promise<CreateOldScenarioResponseDto> => {
	try {
		return await apiClient.post<CreateOldScenarioResponseDto>(
			`/scenario/old`,
			data
		);
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || `기존 시나리오 등록 실패 (HTTP ${error.status})`);
		}
		throw error;
	}
};

/**
 * 중증도분류 결과 조회 (BFF API)
 * GET /{lang}/scenario/result/triage/{location}/{accident}/{triage_method}/{global_sorting}/{random}
 * 
 * 개인 결과(latestScenario, highestScenario)와 전체참가자 데이터(labUsersLatestAvg, demo_patients)를 함께 반환
 * 
 * @param lang - 언어 코드 (ko/en)
 * @param classId - 클래스 ID (헤더에 포함)
 * @param params - 시나리오 설정 파라미터 (results_params에서 파싱)
 * @returns 개인 및 전체참가자 중증도분류 결과
 */
export const getTriageResult = async (
	lang: string,
	classId: string | number,
	params: import('../types/api/scenarioResult').TriageResultsParams
): Promise<import('../types/api/scenarioResult').GetTriageResultResponseDto> => {
	const { location, accident, triage_method, global_sorting, random } = params;
	
	// BFF API는 전체 URL 사용 (https://api2_test.medicrew.me)
	const bffBaseUrl = 'https://api2_test.medicrew.me';
	const path = `/${lang}/scenario/result/lab/stat/triage/${location}/${accident}/${triage_method}/${global_sorting}/${random}`;
	const fullUrl = `${bffBaseUrl}${path}`;
	
	const devLog = isStudyResultDevLogEnabled();
	if (devLog) {
		logStudyResultApiTurn('req', 'BFF GET /scenario/result/triage', {
			url: fullUrl,
			body: { classId, params },
		});
	}
	
	try {
		// BFF API는 직접 fetch 호출 (apiClient baseURL과 다름)
		// 401 이면 토큰 재발급 후 1회 재시도 (재시도 시 최신 토큰 사용)
		const fetchResponse = await fetchWithSessionRefresh(() => {
			const headers: HeadersInit = {
				'x-class-id': String(classId),
				...buildStudyResultWebApiContextHeaders(String(classId)),
			};
			const token = apiClient.getAccessToken();
			if (token) {
				// Bearer 접두사 제거 (토큰만 전송)
				const cleanToken = token.replace(/^Bearer\s+/i, '');
				headers['Authorization'] = cleanToken;
			}
			return fetch(fullUrl, {
				method: 'GET',
				headers,
			});
		});
		
		if (!fetchResponse.ok) {
			const errorData = await fetchResponse.json().catch(() => ({}));
			throw new ApiError(
				fetchResponse.status,
				fetchResponse.statusText,
				errorData,
				errorData.message || `중증도분류 결과 조회 실패 (HTTP ${fetchResponse.status})`
			);
		}
		
		const response = await fetchResponse.json();
		
		if (process.env.NODE_ENV === 'development') {
			console.log('🔍 [getTriageResult] 원본 응답 구조:', {
				hasData: 'data' in response,
				hasStatusCode: 'statusCode' in response,
				topLevelKeys: Object.keys(response),
			});
			
			if (response.data) {
				console.log('🔍 [getTriageResult] response.data 구조:', {
					keys: Object.keys(response.data),
					demoPatientsCount: response.data.demo_patients?.length ?? 0,
					winnersCount: response.data.winners?.length ?? 0,
					hasLabStat: 'lab_stat' in response.data,
				});
			}
		}
		
		// 플랫폼 API 응답 구조: { statusCode, errMsg, data: {...} }
		const data = response?.data ?? response;
		
		if (devLog) {
			logStudyResultApiTurn('res', 'BFF GET /scenario/result/triage', {
				url: fullUrl,
				status: 200,
				body: {
					hasLatestScenario: !!data.latestScenario,
					hasLabUsersLatestAvg: !!data.labUsersLatestAvg,
					demoPatientsCount: data.demo_patients?.length ?? 0,
					winnersCount: data.winners?.length ?? 0,
					playStats: data.play_stats,
					labStat: data.lab_stat,
				},
			});
		}
		
		return data;
	} catch (error) {
		if (devLog) {
			logStudyResultApiTurn('err', 'BFF GET /scenario/result/triage', {
				url: fullUrl,
				err: error,
			});
		}
		if (error instanceof ApiError) {
			throw new Error(error.message || `중증도분류 결과 조회 실패 (HTTP ${error.status})`);
		}
		throw error;
	}
};
